// Finance: monthly P&L, loans/credit line, interest, bankruptcy, reputation, net worth (DESIGN §6.4–6.5).
import { dayToDate } from '../core/time.js';
import { to2025 } from '../data/cpi.js';
import { spotPrice } from '../economy/market.js';
import { eqDef, unitPrice, yf, yearOf } from './defs.js';
import { refiningAssetValue } from './refining.js';
import { hedgeAccountValue } from './m3/hedging.js'; // M3 econ: futures margin accounts & puts
import { clamp, notify, log, spend, earn, nextId, fmt$ } from './util.js';
import { M4_PL_CATS, M4_PL_INCOME } from './m4/marketCore.js'; // M4-B: lazily created P&L lines (gasSales, storage, carbon, carbonTax, credits45q)
import { m4LoanRate } from './m4/marketFinance.js'; // M4-B: base rate + energy HY spread + ESG bps from 2000
import { shaleAssetValue } from './m4/valuation.js'; // M4 balance: shale wells valued on their type curve (prod.shale only exists from 2000)

export const PL_CATS = ['revenue', 'productSales', 'dividends', 'patents', 'recoveries', 'royalties', 'severance', 'wages', 'fuel', 'consumables', 'transport', 'opex', 'crudePurchases', 'refining', 'interest', 'leases', 'capex', 'research', 'security', 'protection', 'insurance', 'licenses', 'fines', 'taxes', 'other',
  'charter', 'logistics', // M3 ops: rig charters (day rates), offshore logistics (boats, helicopters, divers)
  'hedging', 'hostTax', 'windfallTax', 'spills']; // M3 econ: hedge P&L (income, may be negative), host-government tax, WPT, spill cleanup
// M2 (sim/m2/*): recoveries = expropriation compensation, insurance payouts, lawsuit awards (income); severance, protection
// (partner share, bribes, ransom), insurance, licenses (patent fees, licences, legal), fines, taxes (income tax) are costs.
const INCOME = new Set(['revenue', 'productSales', 'dividends', 'patents', 'recoveries', 'hedging']);

export function interpCurve(curve, x) {
  if (!curve?.length) return 1;
  if (x <= curve[0][0]) return curve[0][1];
  for (let i = 1; i < curve.length; i++) {
    if (x <= curve[i][0]) {
      const [x0, y0] = curve[i - 1], [x1, y1] = curve[i];
      return y0 + (y1 - y0) * (x - x0) / (x1 - x0);
    }
  }
  return curve[curve.length - 1][1];
}

export function monthKey(day) {
  const { year, month } = dayToDate(day);
  return `${year}-${String(month).padStart(2, '0')}`;
}

export function newMonth(day) {
  const m = { month: monthKey(day), startDay: day };
  for (const c of PL_CATS) m[c] = 0;
  return m;
}

export function monthNet(m) {
  let net = 0;
  for (const c of PL_CATS) net += INCOME.has(c) ? (m[c] ?? 0) : -(m[c] ?? 0);
  for (const c of M4_PL_CATS) if (m[c]) net += M4_PL_INCOME.has(c) ? m[c] : -m[c]; // M4-B (absent before 2000)
  return net;
}

const RATE_KEYS = [[1860, 7], [1900, 5], [1930, 6], [1950, 4], [1980, 18], [1990, 10], [2010, 5], [2025, 6]];
export function loanRate(state) {
  if (yf(state) >= 2000) return m4LoanRate(state); // M4-B: cost of capital 2000-2029 (M4_SPEC §7)
  const base = interpCurve(RATE_KEYS, yf(state));
  return Math.max(1, base + 3 - 6 * clamp(state.company.reputation, 0, 100) / 100);
}

// ---- valuation --------------------------------------------------------------
export function assetValue(state) {
  let v = 0;
  for (const r of Object.values(state.rigs)) v += unitPrice(state, eqDef(r.defId)) * 0.5 * r.condition / 100;
  for (const i of Object.values(state.items)) {
    const d = eqDef(i.defId);
    if (d) v += unitPrice(state, d) * 0.5 * i.condition / 100;
  }
  for (const [id, s] of Object.entries(state.stock)) { const d = eqDef(id); if (d) v += unitPrice(state, d) * s.qty * 0.5; }
  const spot = spotPrice(state);
  for (const f of Object.values(state.fields)) {
    v += f.storage.bbl * Math.max(0, spot) * 0.7;
    for (const p of f.plots) if (p.owned && !p.wellId) v += (p.leasePaid ?? 0) * 0.7;
  }
  const wells = Object.values(state.wells);
  let shale = false;
  for (const w of wells) { if (w.prod?.shale) { shale = true; continue; } if (w.prod && w.status === 'PRODUCING') v += w.prod.rate * 120 * Math.max(0, spot) * 0.6; }
  if (shale) v += shaleAssetValue(state, wells); // M4 balance: remaining-reserve value of decline-curve shale wells
  v += refiningAssetValue(state);
  v += hedgeAccountValue(state); // M3 econ
  return v;
}

export function debt(state) { return state.company.loans.reduce((a, l) => a + l.principal, 0); }

export function netWorth(state) {
  return state.company.cash + assetValue(state) - debt(state);
}

export function netWorth2025(state) {
  return to2025(netWorth(state), Math.min(yearOf(state), 2025));
}

// Credit available to borrow right now (always a number). creditInfo() has the breakdown.
export function creditLine(state) {
  const v = creditInfo(state).available;
  return Number.isFinite(v) ? v : 0;
}

export function creditInfo(state) {
  const repM = clamp(state.company.reputation / 50, 0.5, 1.5);
  const limit = 0.5 * (assetValue(state) + Math.max(0, state.company.cash)) * repM;
  return { limit, used: debt(state), available: Math.max(0, limit - debt(state)), rate: loanRate(state) };
}

export function takeLoanImpl(state, amount) {
  const amt = Math.round(amount);
  if (!(amt > 0)) return { ok: false, msg: 'Enter an amount.' };
  const cl = creditInfo(state);
  if (amt > cl.available + 0.5) return { ok: false, msg: `The bank will lend at most ${fmt$(cl.available)}.` };
  const loan = { id: nextId(state, 'L'), principal: amt, ratePct: Math.round(cl.rate * 10) / 10, startDay: state.day };
  state.company.loans.push(loan);
  state.company.cash += amt;
  return { ok: true, msg: `Borrowed ${fmt$(amt)} at ${loan.ratePct}%.` };
}

export function repayLoanImpl(state, loanId, amount) {
  const loan = state.company.loans.find((l) => l.id === loanId);
  if (!loan) return { ok: false, msg: 'No such loan.' };
  const amt = Math.min(amount ?? loan.principal, loan.principal, state.company.cash);
  if (!(amt > 0)) return { ok: false, msg: 'Not enough cash.' };
  state.company.cash -= amt;
  loan.principal -= amt;
  if (loan.principal <= 0.01) {
    state.company.loans = state.company.loans.filter((l) => l !== loan);
    state.company.reputation = Math.min(100, state.company.reputation + 1);
    return { ok: true, msg: 'Loan paid off. The bank smiles on you.' };
  }
  return { ok: true, msg: `Repaid ${fmt$(amt)}.` };
}

// ---- daily ------------------------------------------------------------------
// Called at the very start of a tick (date step): closes the previous month's books on the 1st.
export function rollMonth(state, patentIncome = () => 0) {
  const { month, dom } = dayToDate(state.day);
  if (dom === 1) {
    // month close
    if (state.finance.current && state.finance.current.month !== monthKey(state.day)) {
      // interest for the month just ended
      let interest = 0;
      for (const l of state.company.loans) interest += l.principal * l.ratePct / 100 / 12;
      // overdraft: negative cash pays loan rate + 2%
      if (state.company.cash < 0) interest += -state.company.cash * (loanRate(state) + 2) / 100 / 12;
      if (interest > 0) spend(state, interest, 'interest', false);
      const inc = patentIncome(state);
      if (inc > 0) earn(state, inc, 'patents');
      const m = state.finance.current;
      m.net = monthNet(m);
      m.cashEnd = state.company.cash;
      state.finance.history.push(m);
      if (state.finance.history.length > 600) state.finance.history.shift();
      state.finance.current = newMonth(state.day);
      const nw = netWorth2025(state);
      state.company.netWorthHistory.push([state.day, Math.round(nw)]);
      if (state.company.netWorthHistory.length > 1200) state.company.netWorthHistory.shift();
      state.company.stats.peakNetWorth2025 = Math.max(state.company.stats.peakNetWorth2025 ?? 0, nw);
    }
    // yearly safety bonus
    if (month === 1 && state.day - state.company.accidentFreeSince >= 365) {
      state.company.reputation = Math.min(100, state.company.reputation + 1);
      state.company.accidentFreeSince = state.day;
      log(state, 'A full year without accidents: reputation +1.');
    }
  }
}

export function tickFinance(state) {
  // maintenance: 2%/yr of equipment price for rigs
  let maint = 0;
  for (const r of Object.values(state.rigs)) if (!r.mothballed) maint += unitPrice(state, eqDef(r.defId)) * 0.02 / 365;
  if (maint > 0) spend(state, maint, 'other', false);

  // bankruptcy
  if (state.company.cash < 0) {
    state.company.negCashDays++;
    if (state.company.negCashDays === 1) notify(state, 'news', { kind: 'finance', text: `You're overdrawn (${fmt$(state.company.cash)}). Raise cash within 30 days or the bank closes you down.`, level: 'warn' });
    if (state.company.negCashDays >= 30 && creditLine(state) < -state.company.cash && !state.gameOver) {
      state.gameOver = { day: state.day, reason: 'bankrupt' };
      state.paused = true;
      notify(state, 'gameOver', { pause: true, reason: 'bankrupt', text: `${state.company.name} is bankrupt. The sheriff padlocks the derricks.` });
    }
  } else state.company.negCashDays = 0;
}
